import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '../theme/ThemeProvider';
import {
  DashboardIcon,
  FilePlusIcon,
  UsersIcon,
  ContactIcon,
  CatalogIcon,
  ChevronLeftIcon,
  SunIcon,
  MoonIcon,
  ShipIcon,
  PlaneIcon,
  PackageIcon,
  LayersIcon,
  ChartIcon,
  ChevronDownIcon,
  TrailerIcon,
  WarehouseIcon,
  RadarIcon,
  FileReportIcon,
  CpuIcon,
} from '../components/ui/Icons';
import UserMenu from './UserMenu';
import NotificationBell from './NotificationBell';
import Tooltip from '../components/ui/Tooltip/Tooltip';
import CircuitTraces from './CircuitTraces';
import { useDeveloperMode } from './DeveloperModeContext';
import { FEATURE_TRACKING, FEATURE_MULTIMODAL } from '../featureFlags';
import { useAuth } from '../../Components/AuthContext';
import { canOpenModule } from '../lib/modulePermissions';
import styles from './Sidebar.module.css';

type NavLeaf = { to: string; label: string; icon: React.ReactNode };
type NavGroup = {
  /** Identificador del grupo (para el estado de abierto/cerrado). */
  key: string;
  label: string;
  icon: React.ReactNode;
  children: NavLeaf[];
};
type NavEntry = NavLeaf | NavGroup;

const isGroup = (entry: NavEntry): entry is NavGroup =>
  (entry as NavGroup).children !== undefined;

/**
 * Las entradas del menú que esta persona puede abrir (19-oct-2026).
 *
 * Un grupo se queda con los hijos que le tocan, y si no le toca ninguno desaparece entero: un grupo que se abre vacío se
 * lee como un módulo roto.
 *
 * 📌 Qué permiso abre cada módulo vive en `lib/modulePermissions.ts`: los mismos que pide el backend.
 */
const visibleEntries = (entries: NavEntry[], canOpen: (to: string) => boolean): NavEntry[] =>
  entries.flatMap((entry): NavEntry[] => {
    if (!isGroup(entry)) return canOpen(entry.to) ? [entry] : [];
    const children = entry.children.filter((child) => canOpen(child.to));
    return children.length ? [{ ...entry, children }] : [];
  });

/**
 * Grupo de modalidades. Solo aparece con `FEATURE_MULTIMODAL`; ahí la entrada de
 * `/loads` se llama `Terrestrial`, al lado de Ocean y Air. Mientras esté apagado,
 * `/loads` es un ítem suelto (ver `NAV_ITEMS`).
 */
const SHIPMENTS_GROUP: NavGroup = {
  key: 'shipments',
  label: 'Shipments',
  icon: <PackageIcon size={20} />,
  children: [
    { to: '/global-view', label: 'Global View', icon: <LayersIcon size={18} /> },
    { to: '/loads', label: 'Terrestrial', icon: <TrailerIcon size={18} /> },
    { to: '/ocean', label: 'Ocean', icon: <ShipIcon size={18} /> },
    { to: '/air', label: 'Air', icon: <PlaneIcon size={18} /> },
  ],
};

/** 📌 Exportado para las pruebas: cada entrada tiene que tener su renglón en `MODULE_PERMISSIONS`. */
export const NAV_ITEMS: NavEntry[] = [
  { to: '/dashboard', label: 'Dashboard', icon: <DashboardIcon size={20} /> },

  // Con el flag encendido las 3 modalidades viven dentro del grupo Shipments;
  // sin él, se conserva exactamente la entrada original.
  // Global Dashboard va inmediatamente después del principal, y luego el grupo
  // con las vistas de embarques.
  /**
   * 🔄 20-oct-2026: sin el flag, la entrada suelta vuelve a llamarse **Loads**, como en producción: *"regresamos el
   * nombre de terrestrial a Load en el side bar por el momento, ya despues de subir a produccion lo regresamos a
   * terrestrial"*. Dentro del grupo, con el flag encendido, sigue siendo `Terrestrial`.
   *
   * 🔄 26-oct-2026: **se queda `Loads` hasta que suban aéreo y marítimo** (*"Loads es terrestre hasta que suba aereo y
   * maritimo"*). No hay nada que cambiar ese día: al encender `FEATURE_MULTIMODAL` esta entrada suelta desaparece y la
   * de `/loads` vive en el grupo *Shipments*, que ya dice `Terrestrial`.
   */
  ...(FEATURE_MULTIMODAL
    ? [
        {
          to: '/global-dashboard',
          label: 'Global Dashboard',
          icon: <ChartIcon size={20} />,
        } as NavEntry,
        SHIPMENTS_GROUP as NavEntry,
      ]
    : [{ to: '/loads', label: 'Loads', icon: <TrailerIcon size={20} /> } as NavEntry]),

  // Acciones
  { to: '/new-load', label: 'Create Shipment', icon: <FilePlusIcon size={20} /> },
  // Solo visible con el feature flag activo (ver src/v2/featureFlags.ts).
  ...(FEATURE_TRACKING
    ? [{ to: '/tracking', label: 'Track Shipment', icon: <RadarIcon size={20} /> } as NavEntry]
    : []),
  /**
   * Generador del recuadro de PRE-ALERTA aérea, que hoy se arma a mano.
   *
   * 🔄 28-oct-2026: **ya no depende de `FEATURE_MULTIMODAL`** (*"quiero que me ayudes a habilitar la pre alerta para
   * produccion sin habilitar todavia el resto de aereo y maritimo"*). La deciden sus permisos (`PA_VIEW`, en
   * `MODULE_PERMISSIONS`), como los módulos de terrestre. No abre embarques aéreos ni marítimos, y sus endpoints
   * (`/pre-alerts`, `/share-links`) nunca dependieron de la bandera del API.
   */
  { to: '/pre-alert', label: 'Pre-Alert', icon: <FilePlusIcon size={20} /> },

  // Administración
  { to: '/users', label: 'Users', icon: <UsersIcon size={20} /> },
  { to: '/clients', label: 'Clients', icon: <ContactIcon size={20} /> },
  // Bodega, no camión: un "delivery" aquí es una ubicación (shipper/consignee).
  { to: '/deliveries', label: 'Deliveries', icon: <WarehouseIcon size={20} /> },
  { to: '/catalogs', label: 'Catalogs', icon: <CatalogIcon size={20} /> },
  /**
   * 📌 `Activity` ya no vive aquí: se movió dentro de la sección **Desarrollador**
   * (ver `DEVELOPER_GROUP`). La bitácora muestra lo que hicieron todos los
   * usuarios, así que es supervisión, no operación diaria.
   */
];

/**
 * Sección Desarrollador — supervisión y configuración de la plataforma.
 *
 * Aquí van las herramientas que no son operación: la bitácora, y con el tiempo el
 * resto de los controles de plataforma.
 *
 * 🔴 **Ojo con lo que este bloque NO hace: no es seguridad.** Esconderlo del menú
 * es cosmética — las rutas viajan en el bundle del frontend, que es público, así
 * que cualquiera con sesión puede escribir `/audit` en la barra de direcciones. Lo
 * único que protege de verdad es `authorize([DEV_ACCESS])` en el backend, endpoint
 * por endpoint. Esto solo evita el desorden de mostrarle a todos una sección que
 * no les toca.
 */
export const DEVELOPER_GROUP: NavGroup = {
  key: 'developer',
  label: 'Developer',
  icon: <CpuIcon size={20} />,
  children: [
    { to: '/audit', label: 'Activity', icon: <FileReportIcon size={18} /> },
  ],
};

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  /**
   * Drawer abierto en móvil. En escritorio se ignora: ahí el sidebar siempre
   * está visible y lo único que cambia es `collapsed`.
   */
  mobileOpen?: boolean;
  /** Cierra el drawer. Se usa al navegar y al tocar una opción del menú. */
  onCloseMobile?: () => void;
}

/** Ítem simple del menú (con tooltip cuando el sidebar está colapsado). */
const NavItemLink: React.FC<{
  item: NavLeaf;
  collapsed: boolean;
  nested?: boolean;
}> = ({ item, collapsed, nested = false }) => (
  <Tooltip
    content={item.label}
    placement="right"
    disabled={!collapsed}
    className={styles.navTooltip}
  >
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        [styles.item, nested ? styles.itemNested : '', isActive ? styles.itemActive : '']
          .filter(Boolean)
          .join(' ')
      }
    >
      <span className={styles.icon}>{item.icon}</span>
      <span className={styles.label}>{item.label}</span>
    </NavLink>
  </Tooltip>
);

/**
 * Grupo colapsable.
 *
 * Con el sidebar colapsado no se puede mostrar una jerarquía (no hay ancho para
 * el texto), así que los hijos se muestran como iconos sueltos: se conserva el
 * acceso sin romper la lectura.
 */
const NavGroupBlock: React.FC<{
  group: NavGroup;
  collapsed: boolean;
  /**
   * Abrir el grupo solo porque la vista actual le pertenece.
   *
   * Se desactiva en móvil: ahí el menú es un panel que se abre, se usa y se cierra, y llegar
   * con un grupo ya desplegado alarga la lista y empuja el resto de las opciones fuera de la
   * pantalla. La pertenencia sigue visible con el resaltado del propio grupo.
   */
  autoOpen?: boolean;
}> = ({ group, collapsed, autoOpen = true }) => {
  const location = useLocation();
  const hasActiveChild = group.children.some((c) => location.pathname.startsWith(c.to));
  // Arranca abierto si la vista actual pertenece al grupo (solo en escritorio).
  const [open, setOpen] = React.useState(autoOpen && hasActiveChild);

  React.useEffect(() => {
    if (autoOpen && hasActiveChild) setOpen(true);
  }, [autoOpen, hasActiveChild]);

  if (collapsed) {
    return (
      <>
        {group.children.map((child) => (
          <NavItemLink key={child.to} item={child} collapsed />
        ))}
      </>
    );
  }

  return (
    <div className={styles.group}>
      <button
        type="button"
        className={[styles.item, styles.groupToggle, hasActiveChild ? styles.groupActive : '']
          .filter(Boolean)
          .join(' ')}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={styles.icon}>{group.icon}</span>
        <span className={styles.label}>{group.label}</span>
        <span className={[styles.chevron, open ? styles.chevronOpen : ''].join(' ')}>
          <ChevronDownIcon size={15} />
        </span>
      </button>

      {open && (
        <div className={styles.groupChildren}>
          {group.children.map((child, i) => (
            <div
              key={child.to}
              className={styles.groupChild}
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <NavItemLink item={child} collapsed={false} nested />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * ¿Estamos en el ancho donde el sidebar es un drawer?
 *
 * Hace falta en JavaScript y no solo en CSS porque hay decisiones que el CSS no puede tomar:
 * **qué logo cargar** y **si un grupo arranca abierto**. Antes el drawer heredaba el estado
 * `collapsed` guardado en `localStorage`, así que quien colapsó el sidebar en la computadora
 * abría en el teléfono un menú con el logo reducido a símbolo y las opciones sin texto.
 *
 * Se escucha el cambio para que rotar el teléfono o redimensionar la ventana lo reflejen.
 */
const MOBILE_QUERY = '(max-width: 720px)';

const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches
  );

  React.useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    // Se sincroniza al montar por si cambió entre el primer render y el efecto.
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isMobile;
};

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggleCollapse,
  mobileOpen = false,
  onCloseMobile,
}) => {
  const { resolvedTheme, toggle } = useTheme();
  const isMobile = useIsMobile();

  /**
   * La sección Developer se decide en tiempo de render y no en `NAV_ITEMS`, porque
   * `NAV_ITEMS` es una constante de módulo y esto depende de dos hooks.
   *
   * `devMode.enabled` ya combina las dos condiciones: tener `DEV_ACCESS` **y** haber
   * dejado la vista encendida en el interruptor de My account.
   */
  const devMode = useDeveloperMode();

  /**
   * 🔄 19-oct-2026: **solo los módulos que esta persona puede abrir.** *"Entre como cliente y veia las opciones de users
   * deliveries, etc... solo deberia poder ver los modulos a los que tenga acceso"*. Hasta aquí el menú era el mismo para
   * todos, y quien no tenía el permiso abría pantallas que le respondían 403.
   *
   * 📌 Se recalcula cuando cambian los permisos: `AuthContext` los vuelve a leer al renovarse el token, así que darle un
   * módulo a alguien aparece en su menú sin que tenga que volver a entrar.
   */
  const { user } = useAuth();
  const permissions = user?.permissionsActives;
  const navItems = React.useMemo(
    () => visibleEntries(NAV_ITEMS, (to) => canOpenModule(permissions, to)),
    [permissions]
  );
  /** Developer pide además lo de su pantalla (`DEV_ACCESS` y `AU_VIEW`); sin eso, el bloque no sale. */
  const developerGroup = React.useMemo(
    () =>
      visibleEntries([DEVELOPER_GROUP], (to) => canOpenModule(permissions, to))[0] as NavGroup | undefined,
    [permissions]
  );

  /**
   * `collapsed` es un concepto **de escritorio**: en el drawer no aplica, porque un panel que
   * ya ocupa el ancho útil no se contrae. Así el menú móvil siempre se ve como el sidebar
   * extendido: logo completo y opciones con texto.
   */
  const shownCollapsed = collapsed && !isMobile;

  const logoSrc = shownCollapsed
    ? '/Logos DYLO/01_Logotipo DYLO_Logotipo_s-Slogan_recortado.svg'
    : resolvedTheme === 'dark'
    ? '/dylo-logo-dark.svg'
    : '/dylo-logo-light.svg';

  return (
    <aside
      className={[
        styles.sidebar,
        // En móvil el drawer manda; `collapsed` es solo de escritorio.
        shownCollapsed ? styles.collapsed : '',
        mobileOpen ? styles.mobileOpen : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={(e) => {
        // Al tocar una opción del menú, el drawer se cierra solo: en móvil no
        // tiene sentido dejarlo tapando la pantalla tras navegar.
        const target = e.target as HTMLElement;
        if (mobileOpen && target.closest('a')) onCloseMobile?.();
      }}
    >
      {/**
       * Sin encabezado ni botón de cierre: el drawer se cierra tocando fuera, al elegir una
       * opción o con Escape. Se probó con una X y un título "Menu" y sobraban — el logo ya
       * encabeza el panel, y tres formas de cerrar son suficientes sin gastar altura útil.
       */}
      <div className={styles.logoCard}>
        <img className={styles.logo} src={logoSrc} alt="DYLO" />
      </div>

      <div className={styles.navCard}>
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          <ChevronLeftIcon size={16} />
        </button>

        {/* La envoltura existe para anclar el degradado al final del scroll; ver la
            nota de `.navScroll` en el CSS. */}
        <div className={styles.navScroll}>
        <nav className={styles.nav}>
          {navItems.map((entry) =>
            isGroup(entry) ? (
              <NavGroupBlock
                key={entry.key}
                group={entry}
                collapsed={shownCollapsed}
                // En el drawer los grupos arrancan contraídos: ver `autoOpen`.
                autoOpen={!isMobile}
              />
            ) : (
              <NavItemLink key={entry.to} item={entry} collapsed={shownCollapsed} />
            )
          )}
        </nav>
        </div>

      {/**
       * Sección Desarrollador — **fuera** del área que scrollea, justo encima del
       * divisor. Es donde el usuario la pidió ("arriba de Mode"), y estando fija se
       * llega a ella sin importar cuánto haya crecido el menú de arriba.
       *
       * `flex-shrink: 0` en el CSS: si el menú no cabe, el que scrollea es `.nav`,
       * no este bloque.
       */}
      {devMode.enabled && developerGroup && (
        <div className={styles.devBlock}>
          {/* Trazas de circuito de fondo. Van dentro del bloque y con
              `pointer-events: none`, así que no le roban un clic a las opciones. */}
          <CircuitTraces />
          {/* Barrido de escáner: una sola línea recorriendo el bloque de arriba abajo. */}
          <span className={styles.scanline} aria-hidden="true" />
          {/* El contenido va en su propia capa para quedar sobre la decoración. */}
          <div className={styles.devContent}>
            <NavGroupBlock
              group={developerGroup}
              collapsed={shownCollapsed}
              autoOpen={!isMobile}
            />
          </div>
        </div>
      )}

      {/* Sin `.spacer`: `.nav` ya tiene `flex: 1` y absorbe el hueco, así que el
          pie queda abajo tanto si hay dos opciones como si hay veinte. */}
      <div className={styles.footerNav}>
        {/**
         * BANDEJA DE NOTIFICACIONES.
         *
         * 🔑 Va aquí y no en el header porque **el header desaparece**: `GuideDetail` y `UserDetail` llaman a
         * `setHideHeader(true)` y otras pantallas lo reemplazan con `headerContent`. Un globo con el conteo
         * que se apaga al navegar se lee como que los avisos se fueron.
         *
         * ⚠️ El componente **no se dibuja** si su endpoint no está disponible, y no muestra ningún error: se
         * renderiza en todas las pantallas del área autenticada, así que un fallo suyo sería un fallo visible
         * en toda la plataforma.
         */}
        <NotificationBell collapsed={shownCollapsed} />

        <button
          type="button"
          className={[styles.item, styles.modeBtn].join(' ')}
          onClick={toggle}
          title={shownCollapsed ? 'Change theme' : undefined}
        >
          <span className={styles.icon}>
            {resolvedTheme === 'dark' ? <SunIcon size={20} /> : <MoonIcon size={20} />}
          </span>
          <span className={styles.label}>Mode</span>
        </button>

        {/* También con la forma extendida en el drawer: ahí sí hay ancho para el nombre. */}
        <UserMenu collapsed={shownCollapsed} />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
